import {
    archivePlan,
    createPlan,
    duplicatePlan,
    getPlan,
    getPlanStats,
    getPlans,
    setFeatures,
    setLimits,
    updatePlan
} from '@/api/plans';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import type {CreatePlanRequest, UpdatePlanRequest} from '@/types/api';

export function usePlans() {
    return useQuery({
        queryKey: ['plans'],
        queryFn: getPlans
    });
}

export function usePlan(id: string | undefined) {
    return useQuery({
        queryKey: ['plan', id],
        queryFn: () => getPlan(id!),
        enabled: !!id && id !== 'new'
    });
}

export function usePlanStats() {
    return useQuery({
        queryKey: ['plan-stats'],
        queryFn: getPlanStats
    });
}

export function useCreatePlan() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreatePlanRequest) => createPlan(data),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['plans']});
            queryClient.invalidateQueries({queryKey: ['plan-stats']});
            queryClient.invalidateQueries({queryKey: ['pricing']});
        }
    });
}

export function useUpdatePlan() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({id, data}: { id: string; data: UpdatePlanRequest }) => updatePlan(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['plans']});
            queryClient.invalidateQueries({queryKey: ['plan', variables.id]});
            queryClient.invalidateQueries({queryKey: ['plan-stats']});
            queryClient.invalidateQueries({queryKey: ['pricing']});
        }
    });
}

export function useArchivePlan() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => archivePlan(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['plans']});
            queryClient.invalidateQueries({queryKey: ['plan-stats']});
            queryClient.invalidateQueries({queryKey: ['pricing']});
        }
    });
}

export function useSetLimits() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            limits
        }: {
            id: string;
            limits: Record<string, { value: number; error?: string }>;
        }) => setLimits(id, limits),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['plan', variables.id]});
            queryClient.invalidateQueries({queryKey: ['plans']});
            queryClient.invalidateQueries({queryKey: ['plan-stats']});
            queryClient.invalidateQueries({queryKey: ['pricing']});
        }
    });
}

export function useSetFeatures() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            features
        }: {
            id: string;
            features: Record<string, boolean>;
        }) => setFeatures(id, features),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['plan', variables.id]});
            queryClient.invalidateQueries({queryKey: ['plans']});
            queryClient.invalidateQueries({queryKey: ['plan-stats']});
            queryClient.invalidateQueries({queryKey: ['pricing']});
        }
    });
}

export function useDuplicatePlan() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({id, newSlug}: { id: string; newSlug: string }) => duplicatePlan(id, newSlug),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['plans']});
            queryClient.invalidateQueries({queryKey: ['plan-stats']});
            queryClient.invalidateQueries({queryKey: ['pricing']});
        }
    });
}
